import "./shop.css";

import React from "react";
import { Link, useParams } from "react-router-dom";

import { ROUTES, SHOP, categoryPath } from "../../data/content";
import { ARTWORKS } from "../../data/artworks";
import { CATEGORIES, getArtworksBy, getCategory } from "../../data/gallery";
import ArtworkGrid from "../general/ArtworkGrid";
import NotFound from "../general/NotFound";
import PageMeta from "../general/PageMeta";

//"R 127 600,00" -> 127600
const priceValue = (price) => parseFloat(price.replace(/[^\d,]/g, "").replace(",", "."));

const SORTS = [
  { value: "recommended", label: "Recommended" },
  { value: "price-asc", label: "Price (low to high)", compare: (a, b) => priceValue(a.price) - priceValue(b.price) },
  { value: "price-desc", label: "Price (high to low)", compare: (a, b) => priceValue(b.price) - priceValue(a.price) },
  { value: "name-asc", label: "Name A-Z", compare: (a, b) => a.name.localeCompare(b.name) },
  { value: "name-desc", label: "Name Z-A", compare: (a, b) => b.name.localeCompare(a.name) },
];

//Browse the artworks - all of them, or one artist's (the Wix store's category pages, same URLs)
const Shop = () => {
  const { slug } = useParams();
  const [sort, setSort] = React.useState("recommended");
  const isAll = slug === "all-products";
  const category = isAll ? null : getCategory(slug);

  const artworks = React.useMemo(() => {
    const list = isAll ? ARTWORKS : category ? getArtworksBy(category.slug) : [];
    const sorter = SORTS.find((s) => s.value === sort);
    return sorter.compare ? [...list].sort(sorter.compare) : list;
  }, [slug, sort]);

  if (!isAll && !category) {
    return <NotFound />;
  }

  const title = isAll ? SHOP.ALL_TITLE : category.name;
  const sortedCategories = [...CATEGORIES].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className={"page shop"}>
      <PageMeta
        title={isAll ? "Browse Art / Bronzes" : `${category.name} | Artworks`}
        description={isAll ?
          "Browse all artworks at The Charles Greig Gallery - bronze wildlife sculpture and Ardmore ceramics. Contact the gallery to enquire about any piece." :
          `Artworks by ${category.name} at The Charles Greig Gallery, Hyde Park Corner, Johannesburg.`}
      />

      <nav
        className={"breadcrumbs shop-breadcrumbs"}
        aria-label={"Breadcrumb"}
      >
        <Link to={ROUTES.HOME}>Home</Link>
        <span aria-hidden={"true"}>›</span>
        <span aria-current={"page"}>{title}</span>
      </nav>

      <div className={"shop-layout"}>
        <aside className={"shop-sidebar"}>
          <h2 className={"shop-sidebar-title"}>{SHOP.BROWSE_TITLE}</h2>
          <ul className={"shop-categories"}>
            <li>
              <Link
                to={ROUTES.ALL_ARTWORKS}
                className={isAll ? "is-active" : ""}
                aria-current={isAll ? "page" : undefined}
              >
                {SHOP.ALL_TITLE}
              </Link>
            </li>
            {sortedCategories.map((c) => (
              <li key={c.slug}>
                <Link
                  to={categoryPath(c.category)}
                  className={c === category ? "is-active" : ""}
                  aria-current={c === category ? "page" : undefined}
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        <div className={"shop-main"}>
          <img
            className={"shop-banner"}
            src={SHOP.BANNER.src}
            srcSet={`${SHOP.BANNER.small} 1440w, ${SHOP.BANNER.src} 2880w`}
            sizes={"(max-width: 800px) 100vw, calc(100vw - 260px)"}
            alt={""}
            width={2880}
            height={2237}
          />
          <h1 className={"shop-title"}>{title}</h1>
          {category && category.path ? (
            <p className={"shop-artist-link"}>
              <Link to={category.path}>About {category.name}</Link>
            </p>
          ) : null}

          <div className={"shop-toolbar"}>
            <p className={"shop-count"}>{artworks.length} {artworks.length === 1 ? "artwork" : "artworks"}</p>
            <label className={"shop-sort"}>
              <span>Sort by:</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                {SORTS.map((s) => (
                  <option
                    key={s.value}
                    value={s.value}
                  >
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <ArtworkGrid artworks={artworks} />
        </div>
      </div>
    </div>
  );
};

export default Shop;
